import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div>
                <h1 className="text-base sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                  The Course Creator's Vault
                </h1>
                <p className="text-[10px] sm:text-xs text-gray-500">by Templimize AI</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {isAuthenticated ? (
                <>
                  <span className="hidden sm:inline text-gray-700 font-medium text-sm">
                    Welcome, {user?.name}
                  </span>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-3 sm:px-6 py-2 rounded-full font-medium text-sm hover:shadow-lg transform hover:scale-105 transition"
                  >
                    Dashboard
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="text-gray-600 hover:text-gray-900 font-medium transition text-sm"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => navigate('/signup')}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-3 sm:px-6 py-2 rounded-full font-medium text-sm hover:shadow-lg transform hover:scale-105 transition"
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-2 bg-purple-100 rounded-full mb-8">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
            <span className="text-purple-700 font-semibold text-sm">AI-Powered Course Creation</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 mb-6 leading-tight">
            Create Complete Courses
            <br />
            <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              In Minutes, Not Months
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
            The world's most powerful AI course builder. From trending topics to complete video courses,
            sales pages, and marketing campaigns—all generated automatically.
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16">
            <button
              onClick={() => navigate('/signup')}
              className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 py-4 rounded-full text-lg font-bold hover:shadow-2xl transform hover:scale-105 transition"
            >
              Start Creating Now →
            </button>
            <button
              onClick={() => {
                const featuresSection = document.getElementById('features');
                featuresSection?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="w-full sm:w-auto border-2 border-gray-300 text-gray-700 px-10 py-4 rounded-full text-lg font-semibold hover:border-purple-600 hover:text-purple-600 transition"
            >
              Watch Demo
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center space-x-8 text-sm text-gray-500">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              No credit card required
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Setup in 2 minutes
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Cancel anytime
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Tiers */}
      <div className="py-20 bg-gradient-to-b from-white to-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Power Level
            </h2>
            <p className="text-xl text-gray-600">
              From AI assistance to complete automation—we've got you covered
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Basic Tier */}
            <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-200 hover:border-indigo-500 transition transform hover:scale-105">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Basic AI Assistant</h3>
                <p className="text-gray-600">Perfect for getting started with AI-powered course creation</p>
              </div>

              <div className="text-center mb-8">
                <span className="text-5xl font-extrabold text-gray-900">$29</span>
                <span className="text-gray-600">/month</span>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  'AI course outline generation',
                  'Topic suggestions & research',
                  'Basic templates library',
                  'Lesson structuring assistance',
                  'Export to multiple formats',
                  'Email support'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <svg className="w-6 h-6 text-indigo-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate('/signup')}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-full font-bold hover:shadow-xl transform hover:scale-105 transition"
              >
                Start with Basic
              </button>
            </div>

            {/* Premium Tier */}
            <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-2xl p-8 text-white transform hover:scale-105 transition relative">
              <div className="absolute -top-4 right-8 bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full text-sm font-bold">
                MOST POPULAR
              </div>

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">Premium Course Builder</h3>
                <p className="text-purple-100">Complete course creation—100% automated</p>
              </div>

              <div className="text-center mb-8">
                <span className="text-5xl font-extrabold">$47</span>
                <span className="text-purple-200">/month</span>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  '✨ EVERYTHING in Basic, PLUS:',
                  '🚀 Complete video scripts & outlines',
                  '🎯 Trending topic discovery & analysis',
                  '📝 Interactive quizzes & assessments',
                  '💎 Premium template library',
                  '🎨 Sales page builder & copy',
                  '📧 Email campaign sequences',
                  '📱 Social media content pack',
                  '📊 Launch strategy guide',
                  '🎓 Implementation playbook',
                  '⚡ Priority AI processing',
                  '👨‍💼 Priority support + strategy calls'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <svg className="w-6 h-6 text-yellow-300 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-white font-medium">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate('/signup')}
                className="w-full bg-white text-purple-600 py-4 rounded-full font-bold hover:shadow-2xl transform hover:scale-105 transition"
              >
                Get Premium Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Showcase */}
      <div id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Everything You Need to Launch
            </h2>
            <p className="text-xl text-gray-600">
              Not just a course builder—your complete course business in a box
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: '🤖',
                title: 'AI Course Generation',
                description: 'Just input your niche and watch AI create complete course content, structured and ready to sell'
              },
              {
                icon: '📊',
                title: 'Trending Topics',
                description: 'AI analyzes market trends and suggests hot topics in your niche that people are actually searching for'
              },
              {
                icon: '🎥',
                title: 'Video Scripts & Slides',
                description: 'AI-generated video scripts, talking points, and slide outlines ready for you to record'
              },
              {
                icon: '📝',
                title: 'Quizzes & Assessments',
                description: 'Smart quizzes auto-generated for each module to boost engagement and completion rates'
              },
              {
                icon: '🎨',
                title: 'Sales Pages',
                description: 'High-converting sales page layouts with copy written specifically for your course'
              },
              {
                icon: '📧',
                title: 'Marketing Campaigns',
                description: 'Pre-written email sequences and social media campaigns ready to deploy'
              },
              {
                icon: '🚀',
                title: 'Launch Strategy',
                description: 'Step-by-step launch playbook with timelines, checklists, and best practices'
              },
              {
                icon: '💎',
                title: 'Premium Templates',
                description: 'Workbooks, cheat sheets, resource guides—everything your students need to succeed'
              },
              {
                icon: '⚡',
                title: 'Instant Export',
                description: 'Export to PDF, HTML, or platform-ready formats for Teachable & Thinkific'
              }
            ].map((feature, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition border border-gray-100">
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Build Your Course Empire?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Join thousands of creators who've already launched profitable courses with Templimize AI
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="bg-white text-purple-600 px-12 py-5 rounded-full text-xl font-bold hover:shadow-2xl transform hover:scale-105 transition"
          >
            Start Creating Now - It's Free!
          </button>
          <p className="text-purple-200 mt-4">No credit card required • Free tier available forever</p>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Landing;
